import React, { useState, useEffect } from 'react';
import { appPlatform } from '../../../services/appVersion.mjs';
import { Check, Fingerprint, Link2, QrCode, Globe, Shield, Lock, Rocket, Smartphone } from 'lucide-react';
import { colors, FONT, radii } from '../../../theme';
import { S } from '../dashboardStyles';
import { SS } from './settingsStyles';
import { useSettings } from './SettingsContext';
import SettingsScreen from './SettingsScreen';
import NavRow from './NavRow';
import SecurityCard from '../SecurityCard';
import MyDevicesCard from './MyDevicesCard';
import AccountKeyChangedCard from './AccountKeyChangedCard';
import HistoryArchiveCard from '../HistoryArchiveCard';
import { ShareContactSheet } from '../Sheets';
import { api } from '../../../services/api';

/**
 * The seven screens.
 *
 * Content moved as-is from the single scrolling list — one change at a time.
 * Where a text was renamed, it is because the old one named a library and the
 * new one names a consequence, and each of those is called out in place.
 */

// ─── Профиль ────────────────────────────────────────────────────────────────

export function ProfileScreen({ onBack }) {
  const ctx = useSettings();
  const { user, copyLink, onDisplayNameSaved } = ctx;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [share, setShare] = useState(false);

  const save = async () => {
    setEditing(false);
    const next = draft.trim();
    if (next === (user?.display_name || '')) return;
    setError('');
    try {
      const r = await api.setDisplayName(next);
      onDisplayNameSaved?.(r?.display_name ?? next);
    } catch (e) {
      setError(e.message || 'Не удалось сохранить');
    }
  };

  return (
    <SettingsScreen title="Профиль" onBack={onBack} scrollKey="profile">
      <div style={S.settingsCard}>
        <div style={S.settingsRow}>
          <span style={S.settingsLabel}>Имя</span>
          {editing ? (
            <input autoFocus value={draft} maxLength={64}
              onChange={e => setDraft(e.target.value)}
              onBlur={save}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditing(false); }}
              placeholder="Как вас видят друзья"
              style={{
                flex: 1, marginLeft: 12, textAlign: 'right', background: 'transparent',
                border: 'none', outline: 'none', fontSize: 15, color: colors.gray900, fontFamily: FONT,
              }} />
          ) : (
            <button onClick={() => { setDraft(user?.display_name || ''); setEditing(true); }}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 15,
                color: user?.display_name ? colors.gray900 : colors.gray500, fontFamily: FONT,
              }}>
              {user?.display_name || 'Задать имя'}
            </button>
          )}
        </div>
        {error && <div style={{ ...S.settingsRow, color: colors.red, fontSize: 13 }}>{error}</div>}
        <div style={S.settingsRow}>
          <span style={S.settingsLabel}>Логин</span>
          <span style={{ ...S.settingsValue, fontFamily: 'monospace' }}>{user?.username}</span>
        </div>
        <button onClick={() => setShare(true)}
          style={{ ...S.settingsAction, display: 'flex', alignItems: 'center', gap: 8, borderBottom: 'none' }}>
          <QrCode size={18} color={colors.gray900} /> Поделиться контактом
        </button>
      </div>
      <div style={{ ...SS.consequence, padding: '8px 16px' }}>
        Логин выдан случайно, чтобы по нему нельзя было вас найти. Сменить его нельзя.
      </div>

      <div style={S.sectionLabel}>Telegram</div>
      <div style={S.settingsCard}>
        {!ctx.tgLink ? (
          <button onClick={ctx.getTelegramLink}
            style={{ ...S.settingsAction, color: colors.telegram, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link2 size={18} color={colors.telegram} /> Привязать Telegram
          </button>
        ) : (
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 13, color: colors.gray600, marginBottom: 8 }}>Откройте ссылку в Telegram:</div>
            <a href={ctx.tgLink} target="_blank" rel="noreferrer"
              style={{ fontSize: 13, color: colors.telegram, wordBreak: 'break-all' }}>{ctx.tgLink}</a>
            <div style={{ ...SS.consequence, marginTop: 8 }}>Действует 10 минут</div>
          </div>
        )}
      </div>
      <div style={{ ...SS.consequence, padding: '8px 16px' }}>
        Telegram знает ваш номер телефона. После привязки аккаунт связан с ним.
      </div>

      {share && <ShareContactSheet user={user} onClose={() => setShare(false)} onCopy={copyLink} />}
    </SettingsScreen>
  );
}

// ─── Звонки ─────────────────────────────────────────────────────────────────

const PROTOCOLS = [
  { id: 'e2ee', label: 'Зашифрованный (по умолчанию)',
    desc: 'Сервер видит только шифротекст. Работает на заблокированном экране' },
  { id: 'sip', label: 'Обычный (SIP)',
    desc: 'Через сервер телефонии. Он видит разговор. Нужно открытое приложение' },
  { id: 'ask', label: 'Спрашивать каждый раз',
    desc: 'Выбор перед звонком, когда собеседник поддерживает оба' },
];

export function CallsScreen({ onBack, onOpenRoute }) {
  const { callProtocol, onCallProtocolChange, privacyMode, settingsError } = useSettings();
  return (
    <SettingsScreen title="Звонки" onBack={onBack} scrollKey="calls">
      {settingsError && (
        <div style={{ ...SS.consequence, color: colors.red, padding: '0 16px 10px' }}>{settingsError}</div>
      )}
      <div style={S.settingsCard}>
        {PROTOCOLS.map((opt, i) => (
          <button key={opt.id} onClick={() => onCallProtocolChange(opt.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
              padding: '12px 16px', minHeight: 56,
              background: callProtocol === opt.id ? colors.gray100 : 'transparent',
              border: 'none', borderBottom: i < PROTOCOLS.length - 1 ? `1px solid ${colors.gray100}` : 'none',
              cursor: 'pointer', fontFamily: FONT,
            }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: colors.gray900 }}>{opt.label}</div>
              <div style={{ ...SS.consequence, marginTop: 2 }}>{opt.desc}</div>
            </div>
            {callProtocol === opt.id && <Check size={16} color={colors.gray900} />}
          </button>
        ))}
      </div>

      {/* Three things the screen never said, each of which surprises somebody
          exactly once and then costs a support conversation. */}
      <div style={{ ...SS.consequence, padding: '10px 16px 0' }}>
        Спящему собеседнику звонок всегда идёт зашифрованным: обычный требует открытого приложения.
      </div>
      <div style={{ ...SS.consequence, padding: '6px 16px 0' }}>
        Этот выбор получают и гости, звонящие по вашим ссылкам.
      </div>

      <div style={S.sectionLabel}>Соединение</div>
      <div style={S.settingsCard}>
        <NavRow title="Маршрут соединения" value={ROUTE_LABELS[privacyMode] || '—'} onPress={onOpenRoute} last />
      </div>
    </SettingsScreen>
  );
}

// ─── Маршрут соединения ─────────────────────────────────────────────────────
//
// The old labels named the libraries doing the work — "TURN Relay", "TURNS
// (TLS)", "NaCl шифрование" — which tells somebody what we use and nothing
// about what it does to their call. These name the consequence instead.

const ROUTE_LABELS = {
  0: 'Прямое', 1: 'Через сервер', 2: 'Через сервер, замаскировано',
  3: 'Двойная защита служебных', 4: 'Только туннель',
};

const ROUTES = [
  { mode: 0, label: 'Прямое', desc: 'Быстрее всего. Собеседник может увидеть ваш IP-адрес', Icon: Globe },
  { mode: 1, label: 'Через сервер', desc: 'IP скрыт от собеседника. Задержка чуть выше', Icon: Shield },
  { mode: 2, label: 'Через сервер, замаскировано', desc: 'Похоже на обычный сайт. Помогает там, где звонки блокируют', Icon: Lock },
  { mode: 3, label: 'Двойная защита служебных сообщений', desc: 'Соединение переустановится. Идущий звонок прервётся', Icon: Lock, breaksCall: true },
  { mode: 4, label: 'Только туннель', desc: 'Без телефонии вообще. Соединение переустановится', Icon: Rocket, breaksCall: true },
];

export function RouteScreen({ onBack }) {
  const { privacyMode, onPrivacyChange, callActive } = useSettings();
  return (
    <SettingsScreen title="Маршрут соединения" onBack={onBack} scrollKey="route">
      <div style={S.settingsCard}>
        {ROUTES.map((opt, i) => {
          const blocked = callActive && opt.breaksCall;
          return (
            <button key={opt.mode} disabled={blocked}
              onClick={() => !blocked && onPrivacyChange(opt.mode)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                padding: '12px 16px', minHeight: 56,
                background: privacyMode === opt.mode ? colors.gray100 : 'transparent',
                border: 'none', borderBottom: i < ROUTES.length - 1 ? `1px solid ${colors.gray100}` : 'none',
                cursor: blocked ? 'default' : 'pointer', fontFamily: FONT, opacity: blocked ? 0.5 : 1,
              }}>
              <opt.Icon size={20} color={privacyMode === opt.mode ? colors.gray900 : colors.gray400} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 500, color: colors.gray900 }}>{opt.label}</div>
                <div style={{ ...SS.consequence, marginTop: 2 }}>
                  {blocked ? 'Недоступно во время звонка' : opt.desc}
                </div>
              </div>
              {privacyMode === opt.mode && <Check size={16} color={colors.gray900} />}
            </button>
          );
        })}
      </div>
    </SettingsScreen>
  );
}

// ─── Вход и биометрия ───────────────────────────────────────────────────────

export function LoginScreen({ onBack }) {
  const { isNative, nativeFaceIdLinked, linkNativeFaceId, unlinkNativeFaceId, registerWebAuthn } = useSettings();
  const webAuthn = typeof window !== 'undefined' && window.PublicKeyCredential;
  return (
    <SettingsScreen title="Вход и биометрия" onBack={onBack} scrollKey="login">
      <div style={S.settingsCard}>
        {isNative ? (
          nativeFaceIdLinked ? (
            <div style={{ ...S.settingsRow, borderBottom: 'none', justifyContent: 'space-between' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Fingerprint size={18} color={colors.green} /> Face ID привязан
              </span>
              <button onClick={unlinkNativeFaceId}
                style={{ background: 'none', border: 'none', color: colors.red, fontSize: 14, cursor: 'pointer', minHeight: 44 }}>
                Отвязать
              </button>
            </div>
          ) : (
            <button onClick={linkNativeFaceId}
              style={{ ...S.settingsAction, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Fingerprint size={18} color={colors.gray900} /> Привязать Face ID
            </button>
          )
        ) : webAuthn ? (
          <button onClick={registerWebAuthn}
            style={{ ...S.settingsAction, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Fingerprint size={18} color={colors.gray900} /> Добавить Face ID / биометрию
          </button>
        ) : (
          // A dead-end line used to sit here saying "Биометрия недоступна".
          // A list entry that cannot be pressed is furniture; the screen says
          // it once, in prose, and offers nothing to tap.
          <div style={{ ...SS.consequence, padding: 16 }}>
            Этот браузер не умеет биометрию — вход только по паролю.
          </div>
        )}
      </div>
      <div style={{ ...SS.consequence, padding: '8px 16px' }}>
        Отвязка оставит только вход по паролю. Пароль выдавался один раз при регистрации — убедитесь, что он у вас есть.
      </div>
    </SettingsScreen>
  );
}

// ─── Уведомления и фон ──────────────────────────────────────────────────────

export function BackgroundScreen({ onBack }) {
  const { keepAlive, setKeepAlive } = useSettings();
  return (
    <SettingsScreen title="Уведомления и фон" onBack={onBack} scrollKey="background">
      <div style={S.settingsCard}>
        <div style={{ ...S.settingsRow, borderBottom: 'none', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1, paddingRight: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: colors.gray900 }}>Работа в фоне</div>
            <div style={{ ...SS.consequence, marginTop: 2 }}>
              Включено — звонки приходят на свёрнутом приложении, расход батареи выше.
              Выключено — звонок придёт, только когда приложение открыто.
            </div>
          </div>
          <button
            onClick={() => {
              const next = !keepAlive;
              setKeepAlive(next);
              if (window.VoxioxNative?.setKeepAlive) window.VoxioxNative.setKeepAlive(next);
            }}
            style={{
              width: 48, height: 28, borderRadius: 14, border: 'none', padding: 2, flexShrink: 0,
              background: keepAlive ? colors.green : colors.gray200,
              transition: 'background 0.2s', cursor: 'pointer', position: 'relative',
            }}>
            <div style={{
              width: 24, height: 24, borderRadius: 12, background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'transform 0.2s',
              transform: keepAlive ? 'translateX(20px)' : 'translateX(0)',
            }} />
          </button>
        </div>
      </div>
    </SettingsScreen>
  );
}

// ─── Ключи и доверие ────────────────────────────────────────────────────────

export function TrustScreen({ onBack }) {
  const { user, chatDeviceId, impostorNotices = [], acceptNewAccountKey } = useSettings();
  return (
    <SettingsScreen title="Ключи и доверие" onBack={onBack} scrollKey="trust">
      {/* Above everything, because it is the only thing here that is waiting
          on a decision — and because the settings banner promised it. */}
      {acceptNewAccountKey && impostorNotices.map(n => (
        <AccountKeyChangedCard key={n.userId} notice={n} myUserId={user?.id} onAccept={acceptNewAccountKey} />
      ))}
      {/* Own devices first. Who is allowed to be you is a bigger question than
          who you have verified, and it is the one a person can actually act on
          — the fingerprint comparison below is the only check in the whole
          system that no key can perform on their behalf. */}
      <MyDevicesCard userId={user?.id} thisDeviceId={chatDeviceId} />
      <SecurityCard userId={user?.id} />
    </SettingsScreen>
  );
}

// ─── Архив переписки ────────────────────────────────────────────────────────

export function ArchiveScreen({ onBack }) {
  const { user } = useSettings();
  return (
    <SettingsScreen title="Архив переписки" onBack={onBack} scrollKey="archive">
      <HistoryArchiveCard userId={user?.id} />
    </SettingsScreen>
  );
}

// ─── О приложении ───────────────────────────────────────────────────────────

/**
 * Updating without a store: what is running, what is published, one button.
 *
 * Three things this card got wrong on the owner's phone (03.09.2026) and must
 * not get wrong again:
 *  - it printed the release notes from the manifest, so a phone that did NOT
 *    have the build yet displayed a sentence describing it as if it did. The
 *    notes are for whoever writes releases, not for this screen;
 *  - it crammed that sentence into a settings row, which is a flex line, and
 *    the layout broke;
 *  - the button was a grey row among grey rows.
 * So: a short state, and a filled green button only when there is something
 * to install.
 */
function UpdateCard() {
  const native = typeof window !== 'undefined' ? window.VoxioxNative : null;
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!native || !native.updateStatus) return undefined;
    const read = () => { try { setSt(JSON.parse(native.updateStatus())); } catch (e) { setSt(null); } };
    read();
    try { native.checkForUpdate && native.checkForUpdate(); } catch (e) { /* offline */ }
    const t = setInterval(read, 1500);
    return () => clearInterval(t);
  }, [native]);
  if (!native || !native.updateStatus || !st) return null;

  const offer = st.state === 'available' || st.state === 'ready' || st.state === 'downloading';
  const value = st.state === 'checking' ? 'проверяю…'
    : st.state === 'available' ? `есть ${st.versionName}`
    : st.state === 'downloading' ? `${st.percent || 0}%`
    : st.state === 'ready' ? 'файл проверен'
    : st.state === 'failed' ? 'не удалось проверить'
    : 'последняя';

  return (
    <div style={S.settingsCard}>
      <div style={{ ...S.settingsRow, borderBottom: offer ? undefined : 'none' }}>
        <span style={S.settingsLabel}>Обновление</span>
        <span style={{ fontSize: 13, color: st.state === 'failed' ? colors.red : colors.gray600, textAlign: 'right', marginLeft: 12 }}>
          {value}
        </span>
      </div>
      {offer && (
        <div style={{ padding: '10px 16px 12px' }}>
          <button
            onClick={() => { if (st.state !== 'downloading') { try { native.downloadAndInstallUpdate(); } catch (e) { /* nothing to do here */ } } }}
            disabled={st.state === 'downloading'}
            style={{
              width: '100%', border: 'none', borderRadius: radii.md, padding: '13px 16px',
              fontFamily: FONT, fontSize: 15, fontWeight: 600,
              background: st.state === 'downloading' ? colors.gray300 : colors.green,
              color: st.state === 'downloading' ? colors.gray600 : colors.white,
              cursor: st.state === 'downloading' ? 'default' : 'pointer',
            }}>
            {st.state === 'downloading' ? `Загрузка ${st.percent || 0}%`
              : st.state === 'ready' ? 'Установить'
              : `Обновить до ${st.versionName}`}
          </button>
        </div>
      )}
      {st.state === 'failed' && st.why && (
        <div style={{ ...SS.consequence, padding: '0 16px 10px', color: colors.gray600 }}>{st.why}</div>
      )}
    </div>
  );
}

export function AboutScreen({ onBack }) {
  const { user, APP_VERSION, BUILD_DATE, isInstalled, installPrompt, handleInstall } = useSettings();
  return (
    <SettingsScreen title="О приложении" onBack={onBack} scrollKey="about">
      <UpdateCard />
      <div style={S.settingsCard}>
        <div style={S.settingsRow}>
          <span style={S.settingsLabel}>Версия</span>
          <span style={{ fontSize: 13, color: colors.gray600, fontFamily: 'monospace' }}>
            {APP_VERSION} · {BUILD_DATE} · {appPlatform()}
          </span>
        </div>
        <div style={S.settingsRow}>
          <span style={S.settingsLabel}>SIP номер</span>
          <span style={S.settingsValue}>{user?.sip_extension}</span>
        </div>
        <div style={{ ...S.settingsRow, borderBottom: 'none' }}>
          <span style={S.settingsLabel}>Роль</span>
          <span style={S.settingsValue}>{user?.role === 'admin' ? 'Администратор' : 'Пользователь'}</span>
        </div>
      </div>
      {!isInstalled && installPrompt && (
        <div style={S.settingsCard}>
          <button onClick={handleInstall}
            style={{ ...S.settingsAction, display: 'flex', alignItems: 'center', gap: 8, borderBottom: 'none' }}>
            <Smartphone size={18} color={colors.gray900} /> Установить приложение
          </button>
        </div>
      )}
      {isInstalled && (
        <div style={{ ...SS.consequence, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Check size={16} color={colors.green} /> Установлено
        </div>
      )}
    </SettingsScreen>
  );
}
